import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { CButton, CButtonGroup } from '@coreui/react-pro'
import './PanelDiagnosisCard.scss'
import FEATURES from '../../../../../utils/featuresConstants'
import AiCodeCheckSpinner from '../sharedPanelComponents/AiCodeCheckSpinner'
import PanelDiagnosisSearchBox from './PanelDiagnosisSearchBox'
import PanelDiagnosisDisplay from './PanelDiagnosisDisplay'
import { getPCCConditions } from '../../../../../helper/pccApiHelper'
import { getDiagnosisByCodes } from '../../../../../helper/backendHelper'
import PanelSelectedDiagnosisDisplay from './PanelSelectedDiagnosisDisplay'
import { saveEncounterField } from '../../../../../redux/sidePanel/sidePanelThunk'
import { debouncer, isPrimaryAIDiagnosis } from '../sharedPanelComponents/sharedAIfunctions'
import {
  setAiDxCodes,
  setDxAutoSelected,
  setEncounterField,
} from '../../../../../redux/sidePanel/sidePanelSlice'
import { getIsAi } from '../../../../../utils/sharedFeaturesFunctions'
import { setDiagnosisCode } from '../../../../../redux/progressNote/progressNoteSlice'

const DX_TABS = {
  SELECTED: 'SELECTED',
  AI: 'AI',
  FACILITY: 'FACILITY',
  PREVIOUS: 'PREVIOUS',
  SEARCH: 'SEARCH',
}

export default function PanelDiagnosisCard() {
  const dispatch = useDispatch()
  const features = useSelector((state) => state.user.features)
  const providerId = useSelector((state) => state?.user?.provider?._id)
  const settings = useSelector((state) => state.user?.settings)

  const selectedDx = useSelector((state) => state.sidePanel.panelEncounter?.diagnosisCodes) || []
  const dxAutoSelected = useSelector((state) => state.sidePanel.dxAutoSelected)
  const patientId = useSelector((state) => state.sidePanel.panelEncounter?.patient_id)
  const isPCC = useSelector((state) => state.sidePanel.panelEncounter?.points_click_care_patient)
  const sharedServiceProviderId = useSelector(
    (state) => state.sidePanel.panelEncounter?.incident_to_provider_id,
  )
  const encDOS = useSelector((state) => state.sidePanel.panelEncounter?.service_date_from)
  const coding_ai = useSelector((state) => state.sidePanel.panelEncounter?.coding_ai)
  const aiDxCodes = useSelector((state) => state.sidePanel.panelEncounter?.aiDxCodes)
  const aiMobileDiagnosisCodes =
    useSelector((state) => state.sidePanel.panelEncounter?.aiDiagnosisCodes) || []

  const allVisits = useSelector((state) => state.sidePanel.panelEncHistory)
  const isReadOnly = useSelector((state) => state.sidePanel.isReadOnly)
  const isLastEncounter = useSelector((state) => state.sidePanel.isLastEncounter)
  const aiCodeCheckFiring = useSelector((state) => state.sidePanel.aiCodeCheckFiring)

  const panelChart = useSelector((state) => state.sidePanel.panelChart)
  const chartSignedOn = useSelector((state) => state.sidePanel.panelChart?.signed_on)
  const chartSignedUserId = useSelector((state) => state.sidePanel.panelChart?.signed_user_id)
  const chartCosignedOn = useSelector((state) => state.sidePanel.panelChart?.cosigned_on)

  const noteClosed = sharedServiceProviderId
    ? chartCosignedOn || (chartSignedOn && providerId === chartSignedUserId)
    : chartSignedOn

  const [showRationale, setShowRationale] = useState(false)

  const [dxTab, setDxTab] = useState(DX_TABS.SELECTED)

  const [dxHistory, setDxHistory] = useState([])
  const [pccDx, setPccDx] = useState([])
  const [convertedMobileAiDxCodes, setConvertedMobileAiDxCodes] = useState([])

  useEffect(() => {
    const dxHistoryMap = {}
    const history = []
    allVisits?.forEach(({ diagnosisCodeList, service_date_from }) => {
      if (service_date_from === encDOS) {
        return
      }
      const uniqueDXs = []
      diagnosisCodeList?.forEach((dx) => {
        if (!dxHistoryMap[dx.code]) {
          dxHistoryMap[dx.code] = dx.code
          uniqueDXs.push(dx)
        }
      })
      if (uniqueDXs.length > 0) {
        history.push({
          date: service_date_from,
          dxs: uniqueDXs,
        })
      }
    })
    setDxHistory(history)
    // experimental
  }, [allVisits?.length])

  const pccDosValue = Boolean(settings.pccDosFilter) ? encDOS : null
  useEffect(() => {
    if (patientId && isPCC) {
      getPCCConditions(patientId, pccDosValue).then(async (results) => {
        if (results && results.length > 0) {
          let selected = results
            .filter((c) => c.clinicalStatus === 'ACTIVE')
            .map((c) => c.icd10.replaceAll('.', ''))
          let pccCodes = await getDiagnosisByCodes(selected)
          setPccDx(pccCodes?.results || [])
        }
      })
    }
  }, [patientId, isPCC, pccDosValue])

  useEffect(() => {
    if (aiCodeCheckFiring) {
      setDxTab(DX_TABS.AI)
    }
  }, [aiCodeCheckFiring])

  useEffect(() => {
    if (coding_ai?.diagnosis_codes?.length > 0) {
      getAIDxCodes(coding_ai?.diagnosis_codes)
    }
  }, [coding_ai?.diagnosis_codes])

  useEffect(() => {
    if (selectedDx.length === 0 && aiDxCodes?.length > 0 && !dxAutoSelected) {
      dispatch(setDxAutoSelected())
      setTimeout(() => updateEncounter(aiDxCodes), 1000)
    }
  }, [aiDxCodes, selectedDx, dxAutoSelected])

  useEffect(() => {
    if (aiMobileDiagnosisCodes.length > 0) {
      getDiagnosisByCodes(aiMobileDiagnosisCodes).then((res) => {
        setConvertedMobileAiDxCodes(res?.results || [])
      })
    }
  }, [aiMobileDiagnosisCodes])

  const updateEncounter = (newDx) => {
    dispatch(
      setEncounterField({
        field: 'diagnosisCodes',
        value: newDx,
      }),
    )
    if (!noteClosed && panelChart) {
      dispatch(setDiagnosisCode(newDx))
    }
    debouncer(() => dispatch(saveEncounterField({})))
  }

  const onDxToggle = (item) => {
    let newDx = []
    if (!selectedDx.find((diagnosis) => diagnosis.code === item.code)) {
      newDx = [...selectedDx, item]
    } else {
      newDx = selectedDx.filter((dx) => dx.code !== item.code)
    }

    updateEncounter(newDx)
  }

  const onRemoveAll = () => {
    updateEncounter([])
  }

  const getAIDxCodes = async (codes) => {
    const res = await getDiagnosisByCodes(codes)
    dispatch(setAiDxCodes(res?.results))
  }

  const canUseAi = getIsAi() && features?.includes(FEATURES.DesktopAI)

  return (
    <div id={'PanelDiagnosisCard'} className={'d-flex flex-column gap-2'}>
      {canUseAi &&
        !aiCodeCheckFiring &&
        ((dxTab === DX_TABS.SELECTED &&
          isPrimaryAIDiagnosis(coding_ai, features, selectedDx?.[0]?.code)) ||
          (dxTab === DX_TABS.AI &&
            aiDxCodes?.some((dx) => isPrimaryAIDiagnosis(coding_ai, features, dx?.code)))) && (
          <div
            className={'display-box position-relative'}
            onClick={() => setShowRationale(!showRationale)}
          >
            <span className={`rationale ${!showRationale ? 'condensed' : ''}`}>
              {coding_ai?.anthropic?.information}
            </span>
            <div className={`${showRationale ? 'show-less' : 'show-more'}`}>
              {showRationale && <i className="fa-solid fa-chevron-up"></i>}
              {!showRationale && <i className="fa-solid fa-chevron-down"></i>}
            </div>
          </div>
        )}

      <CButtonGroup id={'dx-tab-group'}>
        <CButton
          color={dxTab === DX_TABS.SELECTED ? 'primary' : ''}
          onClick={() => setDxTab(DX_TABS.SELECTED)}
        >
          Selected ({selectedDx?.length || 0})
        </CButton>
        {canUseAi && (
          <CButton
            color={dxTab === DX_TABS.AI ? 'primary' : ''}
            onClick={() => setDxTab(DX_TABS.AI)}
          >
            AI ({(aiDxCodes?.length || 0) + convertedMobileAiDxCodes?.length || 0})
          </CButton>
        )}
        <CButton
          color={dxTab === DX_TABS.FACILITY ? 'primary' : ''}
          onClick={() => setDxTab(DX_TABS.FACILITY)}
        >
          FAC ({pccDx?.length || 0})
        </CButton>
        <CButton
          color={dxTab === DX_TABS.PREVIOUS ? 'primary' : ''}
          onClick={() => setDxTab(DX_TABS.PREVIOUS)}
        >
          Previous ({dxHistory.reduce((sum, obj) => sum + obj?.dxs?.length || 0, 0)})
        </CButton>
        {!(isReadOnly && !isLastEncounter) && (
          <CButton
            color={dxTab === DX_TABS.SEARCH ? 'primary' : ''}
            onClick={() => setDxTab(DX_TABS.SEARCH)}
          >
            Search
          </CButton>
        )}
      </CButtonGroup>
      {isReadOnly && <div className={'locked'}></div>}

      {dxTab === DX_TABS.SELECTED && (
        <PanelSelectedDiagnosisDisplay
          onDxToggle={onDxToggle}
          onRemoveAll={onRemoveAll}
          onUpdateAll={updateEncounter}
          dxHistory={dxHistory}
        />
      )}
      {dxTab === DX_TABS.AI && (
        <>
          {aiCodeCheckFiring ? (
            <div className={'aiSpinner'} style={{ marginTop: '8em' }}>
              <AiCodeCheckSpinner />
            </div>
          ) : (
            <PanelDiagnosisDisplay
              dxTabList={[...(aiDxCodes || []), ...convertedMobileAiDxCodes]}
              onDxToggle={onDxToggle}
              dxHistory={dxHistory}
              onMassUpdate={updateEncounter}
            />
          )}
        </>
      )}
      {dxTab === DX_TABS.FACILITY && (
        <PanelDiagnosisDisplay
          dxTabList={pccDx}
          onDxToggle={onDxToggle}
          onMassUpdate={updateEncounter}
        />
      )}
      {dxTab === DX_TABS.PREVIOUS && (
        <PanelDiagnosisDisplay
          dxTabList={dxHistory}
          onDxToggle={onDxToggle}
          onMassUpdate={updateEncounter}
          isHistory
        />
      )}
      {dxTab === DX_TABS.SEARCH && !(isReadOnly && !isLastEncounter) && (
        <PanelDiagnosisSearchBox selectedDx={selectedDx} onDxToggle={onDxToggle} />
      )}
    </div>
  )
}
